import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money } from "@/lib/utils-app";
import { toast } from "sonner";
import { Trash2, Upload, Plus, X, Image as ImageIcon, Printer, Ban, FileText, History } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";

const REASON_LABEL = {
  desistencia: "Desistência do cliente",
  falta_peca: "Falta de peça",
  outro: "Outro motivo",
};

export default function OSEditModal({ osCode, onClose }) {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const isCEO = isSuper && (user?.tags || []).includes("ceo");
  const [os, setOs] = useState(null);
  const [diag, setDiag] = useState("");
  const [status, setStatus] = useState("");
  const [labor, setLabor] = useState(0);
  const [parts, setParts] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState({ reason: "desistencia", note: "", requires_refund: false });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const fileRef = useRef();

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/admin/service-orders/${osCode}`);
    setOs(data);
    setDiag(data.diagnostic || "");
    setStatus(data.status);
    setLabor(data.labor_cost || 0);
    setParts(data.parts || []);
    if (data.customer_doc) loadHistory(data.customer_doc);
  }, [osCode]);
  useEffect(() => { load(); }, [load]);

  const partsTotal = parts.reduce((s, p) => s + (Number(p.unit_price) || 0) * (Number(p.qty) || 1), 0);
  const total = partsTotal + Number(labor || 0);

  const save = async () => {
    try {
      await api.patch(`/admin/service-orders/${osCode}`, { diagnostic: diag, parts, labor_cost: Number(labor), status });
      toast.success("OS atualizada.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const genCharge = async () => {
    try {
      await api.post(`/admin/service-orders/${osCode}/generate-charge`);
      toast.success("Cobrança gerada. OS movida para Pagamentos Pendentes.");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const requestCancel = async () => {
    try {
      await api.post(`/admin/service-orders/${osCode}/request-cancellation`, cancelForm);
      toast.success("Solicitação de cancelamento enviada.");
      setCancelOpen(false); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const deleteOs = async () => {
    try {
      await api.delete(`/admin/service-orders/${osCode}`);
      toast.success("OS excluída.");
      setDeleteOpen(false); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); setDeleteOpen(false); }
  };

  const printFull = async () => {
    try {
      const resp = await api.get(`/admin/service-orders/${osCode}/full-print`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) window.location.href = url; // popup blocked → same tab fallback
    } catch (e) { toast.error("Falha ao gerar PDF"); }
  };

  const loadHistory = async (doc) => {
    if (!doc) return;
    try {
      const { data } = await api.get(`/admin/customers/history`, { params: { doc, exclude_os: osCode } });
      setHistory(data);
    } catch (e) { logError("[history]", e); setHistory({ total: 0, total_spent: 0, items: [] }); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData(); form.append("file", file);
    try {
      await api.post(`/admin/service-orders/${osCode}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto enviada.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro upload"); }
    e.target.value = "";
  };

  const delPhoto = async (id) => {
    await api.delete(`/admin/service-orders/${osCode}/photos/${id}`);
    await load();
  };

  if (!os) return null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            <span className="font-mono">{os.os_code}</span> — {os.customer_name} {os.customer_surname}
          </DialogTitle>
          <div className="text-sm text-neutral-500 flex items-center gap-3 flex-wrap">
            <span>{os.device_brand} {os.device_model}</span>
            <span className="font-mono">{os.customer_phone}</span>
            <WhatsAppButton
              phone={os.customer_phone}
              message={`Olá ${os.customer_name}, aqui é da GTec sobre a OS ${os.os_code}.`}
              testid={`edit-wa-${os.os_code}`}
              size="xs"
            />
          </div>
          {os.authorized && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-green-50 text-green-800 border border-green-200 px-3 py-1.5 rounded-full" data-testid="admin-authorized-badge">
              ✓ Autorizado por <b>{os.authorized_by}</b> {os.authorized_at ? `· ${new Date(os.authorized_at).toLocaleString("pt-BR")}` : ""}
            </div>
          )}
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-2">
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Orçamento", "Aguardando Autorização", "Serviço Concluído"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Diagnóstico / Problemas encontrados</Label>
              <Textarea value={diag} onChange={(e) => setDiag(e.target.value)} className="mt-1.5 min-h-[120px]" data-testid="edit-diag" />
            </div>

            {/* Parts table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Peças e serviços</Label>
                <Button size="sm" variant="outline" onClick={() => setParts([...parts, { name: "", qty: 1, unit_price: 0 }])} data-testid="add-part">
                  <Plus size={14} className="mr-1"/> Item
                </Button>
              </div>
              <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                {parts.map((p, i) => (
                  <div key={p._uid ?? `part-${i}`} className="grid grid-cols-12 gap-2 p-2 items-center">
                    <Input value={p.name} onChange={(e) => { const c = [...parts]; c[i] = { ...c[i], name: e.target.value }; setParts(c); }} placeholder="Descrição" className="col-span-6 h-9" data-testid={`part-name-${i}`} />
                    <Input type="number" value={p.qty} onChange={(e) => { const c = [...parts]; c[i] = { ...c[i], qty: Number(e.target.value) }; setParts(c); }} className="col-span-2 h-9 font-mono" data-testid={`part-qty-${i}`} />
                    <Input type="number" step="0.01" value={p.unit_price} onChange={(e) => { const c = [...parts]; c[i] = { ...c[i], unit_price: Number(e.target.value) }; setParts(c); }} className="col-span-3 h-9 font-mono" data-testid={`part-price-${i}`} />
                    <button onClick={() => setParts(parts.filter((_, j) => j !== i))} className="col-span-1 text-neutral-400 hover:text-red-500" data-testid={`part-del-${i}`}><Trash2 size={16}/></button>
                  </div>
                ))}
                {parts.length === 0 && <div className="p-3 text-xs text-neutral-500">Sem itens ainda.</div>}
              </div>
            </div>
            <div>
              <Label>Mão de obra</Label>
              <Input type="number" step="0.01" value={labor} onChange={(e) => setLabor(e.target.value)} className="mt-1.5 h-11 font-mono" data-testid="edit-labor" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">Total automático</div>
              <div className="font-mono text-3xl font-bold mt-1" data-testid="edit-total">{money(total)}</div>
              <div className="text-xs text-neutral-500 mt-1">Peças {money(partsTotal)} + mão de obra {money(labor)}</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-1"><ImageIcon size={14}/> Fotos ({os.photos?.length || 0})</Label>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-photo">
                  <Upload size={14} className="mr-1"/> Enviar
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {os.photos?.map((p) => (
                  <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-neutral-200">
                    <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full grid place-items-center opacity-0 group-hover:opacity-100" data-testid={`del-photo-${p.id}`}><X size={12}/></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer history (mini-CRM) */}
            {history && history.total > 0 && (
              <div className="p-3 rounded-lg border border-neutral-200 bg-neutral-50" data-testid="customer-history">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-1"><History size={14}/> Histórico do cliente ({history.total})</Label>
                  <span className="text-[11px] text-neutral-500">
                    Total gasto: <b className="text-neutral-800 font-mono">R$ {(history.total_spent || 0).toFixed(2)}</b>
                  </span>
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {history.items.slice(0, 8).map((h) => (
                    <div key={h.os_code} className="flex items-center justify-between gap-2 text-[11px] bg-white border border-neutral-200 rounded px-2 py-1.5" data-testid={`history-row-${h.os_code}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-bold text-neutral-900">{h.os_code}</div>
                        <div className="text-neutral-500 truncate">{h.device_brand} {h.device_model} · {(h.created_at || "").slice(0, 10)}</div>
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        {(() => {
                          const pillClass = h.status === "Serviço Concluído"
                            ? "bg-green-100 text-green-800"
                            : h.status === "Aguardando Autorização"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-neutral-100 text-neutral-700";
                          return <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${pillClass}`}>{h.status}</span>;
                        })()}
                        <span className="font-mono text-neutral-700 mt-0.5">R$ {Number(h.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  {history.total > 8 && (
                    <div className="text-[10px] text-neutral-500 text-center pt-1">+ {history.total - 8} OS anteriores</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 justify-end border-t border-neutral-100 pt-4">
          <Button variant="outline" onClick={() => window.open(`/admin/etiqueta/${osCode}`, "_blank", "width=520,height=520")} data-testid="edit-print-label">
            <Printer size={14} className="mr-1.5"/> Etiqueta
          </Button>
          <Button variant="outline" onClick={printFull} data-testid="edit-print-full">
            <FileText size={14} className="mr-1.5"/> Imprimir OS completa
          </Button>
          {os.cancellation_status === "none" && (
            <Button variant="outline" onClick={() => setCancelOpen(true)} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="edit-request-cancel">
              <Ban size={14} className="mr-1.5"/> Solicitar cancelamento
            </Button>
          )}
          {os.cancellation_status === "pending" && (
            <span className="inline-flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-full" data-testid="cancel-pending-badge">
              <Ban size={12}/> Cancelamento pendente ({REASON_LABEL[os.cancellation_reason]})
            </span>
          )}
          {isCEO && (
            <Button variant="outline" onClick={() => setDeleteOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="edit-ceo-delete-os">
              <Trash2 size={14} className="mr-1.5"/> Excluir OS
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800" data-testid="edit-save">Salvar alterações</Button>
          {status === "Serviço Concluído" && !os.charge_generated && os.cancellation_status !== "pending" && (
            <Button onClick={async () => { await save(); await genCharge(); }} className="bg-green-600 hover:bg-green-700" data-testid="gen-charge">
              Gerar Cobrança
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Request cancellation dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Solicitar cancelamento — <span className="font-mono">{osCode}</span></AlertDialogTitle>
            <AlertDialogDescription>Um super-admin precisará aprovar. Descreva o motivo com clareza.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2 block">Motivo</Label>
              <RadioGroup value={cancelForm.reason} onValueChange={(v) => setCancelForm({ ...cancelForm, reason: v })} className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm p-2 border border-neutral-200 rounded-lg cursor-pointer has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-50">
                  <RadioGroupItem value="desistencia" data-testid="cancel-reason-desistencia" /> Desistência do cliente
                </label>
                <label className="flex items-center gap-2 text-sm p-2 border border-neutral-200 rounded-lg cursor-pointer has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-50">
                  <RadioGroupItem value="falta_peca" data-testid="cancel-reason-falta_peca" /> Falta de peça / não encontramos reposição
                </label>
                <label className="flex items-center gap-2 text-sm p-2 border border-neutral-200 rounded-lg cursor-pointer has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-50">
                  <RadioGroupItem value="outro" data-testid="cancel-reason-outro" /> Outro motivo
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-neutral-500">Observação</Label>
              <Textarea rows={3} value={cancelForm.note} onChange={(e) => setCancelForm({ ...cancelForm, note: e.target.value })} className="mt-1.5" placeholder="Detalhes que ajudem o super-admin a decidir" data-testid="cancel-note-input" />
            </div>
            <label className="flex items-center gap-2 text-sm p-3 border border-neutral-200 rounded-lg cursor-pointer">
              <Checkbox checked={cancelForm.requires_refund} onCheckedChange={(v) => setCancelForm({ ...cancelForm, requires_refund: !!v })} data-testid="cancel-refund-check" />
              <span>Cliente <b>já pagou</b> e precisa de estorno</span>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={requestCancel} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cancel-submit">Enviar solicitação</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CEO delete dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Excluir OS <span className="font-mono">{osCode}</span></AlertDialogTitle>
            <AlertDialogDescription>
              Ação exclusiva do CEO. Esta OS e todo o seu histórico serão apagados permanentemente. Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteOs} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ceo-delete-confirm">Sim, excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
